import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, Circle, Lock, ArrowRight, BookOpen, Zap, Code2, ChevronDown, ChevronUp, ExternalLink } from 'lucide-react';
import { reactRoadmap } from '../../data';

const completedDays = new Set([1, 2, 3, 4, 5, 6]);

const phaseColors = ['#00e5c8', '#8b5cf6', '#f59e0b', '#10b981', '#f43f5e', '#f59e0b'];

const lessonPreviews: Record<string, { what: string; mental: string; code: string; takeaway: string }> = {
  'fiber-architecture': {
    what: 'React Fiber is the reconciliation engine introduced in React 16. It replaces the old recursive stack-based rendering with a linked list of "fiber nodes" — making rendering interruptible.',
    mental: 'Think of Fiber as a priority queue of work. React picks the most urgent task, does a bit of it, and checks if the browser needs the thread back. If yes — pause. This is how React achieves 60fps even with heavy trees.',
    code: `// Each fiber node links to its children, siblings, and parent
interface Fiber {
  type: string | Function;
  child: Fiber | null;      // first child
  sibling: Fiber | null;    // next sibling  
  return: Fiber | null;     // parent (not "parent" to avoid confusion)
  alternate: Fiber | null;  // the "double buffer" — current vs WIP
  effectTag: 'PLACEMENT' | 'UPDATE' | 'DELETION';
}`,
    takeaway: 'Render functions can run multiple times. Keep them pure — no side effects, no mutations.',
  },
  'reconciliation': {
    what: 'Reconciliation is the algorithm React uses to diff the new virtual DOM tree against the previous one, deciding the minimum set of DOM mutations needed.',
    mental: 'React uses two heuristics: (1) Two elements of different types produce different trees — React destroys the old and builds fresh. (2) Keys tell React which list items match across renders.',
    code: `// Without keys — React can't track identity
items.map(item => <Row data={item} />)  // ❌ thrashes on reorder

// With keys — stable identity, only moves in DOM
items.map(item => <Row key={item.id} data={item} />)  // ✅`,
    takeaway: 'Keys must be stable, unique, and from your data — never array index for dynamic lists.',
  },
};

function TopicRow({ topic, phaseColor }: { topic: any; phaseColor: string }) {
  const [expanded, setExpanded] = useState(false);
  const isCompleted = completedDays.has(topic.day);
  const preview = lessonPreviews[topic.slug];

  return (
    <div style={{ borderBottom: '1px solid var(--border)' }}>
      <div
        onClick={() => preview && setExpanded(e => !e)}
        style={{
          display: 'flex', alignItems: 'center', gap: 14, padding: '14px 20px',
          cursor: preview ? 'pointer' : 'default',
          transition: 'background 0.15s',
          background: expanded ? 'var(--surface)' : 'transparent',
        }}
        onMouseOver={e => { if (!expanded) (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.02)'; }}
        onMouseOut={e => { if (!expanded) (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
      >
        {isCompleted
          ? <CheckCircle2 size={16} style={{ color: phaseColor, flexShrink: 0 }} />
          : topic.status === 'coming-soon'
            ? <Lock size={15} style={{ color: 'var(--text3)', flexShrink: 0 }} />
            : <Circle size={16} style={{ color: 'var(--text3)', flexShrink: 0 }} />
        }
        <span style={{
          fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text3)',
          minWidth: 40, flexShrink: 0,
        }}>Day {topic.day}</span>
        <span style={{
          fontSize: 14, fontWeight: isCompleted ? 500 : 400,
          color: isCompleted ? 'var(--text)' : 'var(--text2)',
          flex: 1,
        }}>{topic.title}</span>
        {isCompleted && <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: phaseColor, background: `${phaseColor}15`, padding: '2px 8px', borderRadius: 4 }}>Done</span>}
        {topic.status === 'coming-soon' && <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--text3)' }}>Soon</span>}
        {preview && (expanded ? <ChevronUp size={14} style={{ color: 'var(--text3)', flexShrink: 0 }} /> : <ChevronDown size={14} style={{ color: 'var(--text3)', flexShrink: 0 }} />)}
      </div>

      {expanded && preview && (
        <div style={{ padding: '0 20px 20px 50px', borderTop: '1px solid var(--border)' }}>
          <div style={{ marginTop: 20, display: 'grid', gap: 16 }}>
            <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, padding: 18 }}>
              <p style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: phaseColor, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>What it is</p>
              <p style={{ fontSize: 14, color: 'var(--text2)', lineHeight: 1.7 }}>{preview.what}</p>
            </div>
            <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, padding: 18 }}>
              <p style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: '#f59e0b', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>Mental Model</p>
              <p style={{ fontSize: 14, color: 'var(--text2)', lineHeight: 1.7 }}>{preview.mental}</p>
            </div>
            <div style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 10, padding: 18, overflow: 'hidden' }}>
              <p style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: '#8b5cf6', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12 }}>Code Example</p>
              <pre style={{ margin: 0, overflow: 'auto' }}><code style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text2)', lineHeight: 1.7 }}>{preview.code}</code></pre>
            </div>
            <div style={{ borderLeft: `3px solid ${phaseColor}`, paddingLeft: 14 }}>
              <p style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: phaseColor, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>Key Takeaway</p>
              <p style={{ fontSize: 14, color: 'var(--text)', lineHeight: 1.6, fontWeight: 500 }}>{preview.takeaway}</p>
            </div>
          </div>
          <Link to={`/blog/react-fiber-explained`} style={{
            display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 16,
            color: phaseColor, fontSize: 13, fontWeight: 500,
          }}>
            Read full article <ExternalLink size={12} />
          </Link>
        </div>
      )}
    </div>
  );
}

function PhaseCard({ phase, index }: { phase: any; index: number }) {
  const [open, setOpen] = useState(index === 0);
  const color = phaseColors[index] || '#00e5c8';
  const completedCount = phase.topics.filter((t: any) => completedDays.has(t.day)).length;
  const pct = Math.round((completedCount / phase.topics.length) * 100);

  return (
    <div style={{
      background: 'var(--bg2)', border: '1px solid var(--border)',
      borderRadius: 'var(--radius)', overflow: 'hidden',
      transition: 'border 0.2s',
    }}>
      {/* Phase Header */}
      <div
        onClick={() => setOpen(o => !o)}
        style={{
          padding: '22px 24px', cursor: 'pointer', display: 'flex',
          alignItems: 'center', justifyContent: 'space-between', gap: 16,
        }}
      >
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              Phase {phase.id} · Days {phase.days}
            </span>
            {pct === 100 && <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: '#10b981', background: 'rgba(16,185,129,0.1)', padding: '2px 8px', borderRadius: 4 }}>Complete</span>}
          </div>
          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 700, letterSpacing: '-0.01em', marginBottom: 4 }}>{phase.title}</h3>
          <p style={{ color: 'var(--text2)', fontSize: 13, lineHeight: 1.5 }}>{phase.description}</p>
        </div>
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          <span style={{ fontFamily: 'var(--font-display)', fontSize: 24, fontWeight: 800, color: pct > 0 ? color : 'var(--text3)' }}>{pct}%</span>
          <p style={{ color: 'var(--text3)', fontSize: 11, fontFamily: 'var(--font-mono)' }}>{completedCount}/{phase.topics.length} days</p>
        </div>
      </div>

      {/* Progress Bar */}
      <div style={{ height: 2, background: 'var(--border)', margin: '0 24px' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: color, transition: 'width 0.5s ease', borderRadius: 1 }} />
      </div>

      {/* Topics */}
      {open && (
        <div style={{ marginTop: 8 }}>
          {phase.topics.map((topic: any) => (
            <TopicRow key={topic.day} topic={topic} phaseColor={color} />
          ))}
        </div>
      )}
    </div>
  );
}

export default function ReactTutorial() {
  const totalCompleted = completedDays.size;
  const totalDays = reactRoadmap.totalDays;
  const overallPct = Math.round((totalCompleted / totalDays) * 100);

  return (
    <div style={{ paddingTop: 64, minHeight: '100vh' }}>
      {/* Hero */}
      <section style={{ padding: '72px 24px 48px', position: 'relative', overflow: 'hidden' }}>
        <div style={{
          position: 'absolute', top: -100, left: '30%',
          width: 500, height: 500, borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(0,229,200,0.07) 0%, transparent 70%)',
          pointerEvents: 'none',
        }} />
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <div className="badge" style={{ marginBottom: 20 }}>
            <Code2 size={11} /> React Mastery Guide
          </div>
          <h1 style={{
            fontFamily: 'var(--font-display)', fontSize: 'clamp(36px, 5vw, 64px)',
            fontWeight: 800, lineHeight: 1.05, letterSpacing: '-0.03em', marginBottom: 16,
          }}>
            React Internals &<br />
            <span style={{ color: 'var(--cyan)' }}>Performance Mastery</span>
          </h1>
          <p style={{ color: 'var(--text2)', fontSize: 18, maxWidth: 580, lineHeight: 1.7, marginBottom: 32 }}>
            A 49-day deep dive from Fiber architecture to production-grade dashboards. Written as I prepare for senior roles at world-class companies.
          </p>

          {/* Stats Row */}
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            {[
              { label: 'Total Days', value: totalDays },
              { label: 'Completed', value: totalCompleted },
              { label: 'Progress', value: `${overallPct}%` },
              { label: 'Phases', value: reactRoadmap.phases.length },
            ].map(s => (
              <div key={s.label} style={{
                padding: '14px 22px', background: 'var(--surface)', border: '1px solid var(--border)',
                borderRadius: 'var(--radius-sm)', textAlign: 'center',
              }}>
                <div style={{ fontFamily: 'var(--font-display)', fontSize: 24, fontWeight: 800, color: 'var(--cyan)' }}>{s.value}</div>
                <div style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '0.08em', marginTop: 2 }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About This Guide */}
      <section style={{ padding: '0 24px 48px' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <div style={{
            padding: 28, background: 'var(--surface)', border: '1px solid var(--border)',
            borderRadius: 'var(--radius)', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 24,
          }}>
            {[
              { icon: <BookOpen size={18} />, title: 'First-Principles', desc: 'Every topic starts with why — not just how. Mental models before code.' },
              { icon: <Code2 size={18} />, title: 'Real Code', desc: 'Examples from actual production apps — Syndr, Pump Market Cap, and SigNoz integrations.' },
              { icon: <Zap size={18} />, title: 'Interview-Ready', desc: 'Each day ends with a battle-tested answer you can deliver confidently in any interview.' },
            ].map(item => (
              <div key={item.title} style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                <div style={{ color: 'var(--cyan)', flexShrink: 0, marginTop: 2 }}>{item.icon}</div>
                <div>
                  <p style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 15, marginBottom: 4 }}>{item.title}</p>
                  <p style={{ color: 'var(--text2)', fontSize: 13, lineHeight: 1.6 }}>{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Roadmap */}
      <section style={{ padding: '0 24px 80px' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <p style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text3)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 24 }}>
            Curriculum · Click any phase to expand
          </p>
          <div style={{ display: 'grid', gap: 16 }}>
            {reactRoadmap.phases.map((phase, i) => (
              <PhaseCard key={phase.id} phase={phase} index={i} />
            ))}
          </div>

          {/* CTA */}
          <div style={{
            marginTop: 48, padding: 32, background: 'linear-gradient(135deg, rgba(0,229,200,0.07), rgba(139,92,246,0.07))',
            border: '1px solid rgba(0,229,200,0.2)', borderRadius: 'var(--radius)', textAlign: 'center',
          }}>
            <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 24, fontWeight: 700, marginBottom: 10 }}>
              Want to learn together?
            </h3>
            <p style={{ color: 'var(--text2)', fontSize: 15, marginBottom: 24, maxWidth: 460, margin: '0 auto 24px' }}>
              I'm writing detailed posts for each day as I complete them. Follow along on the blog or reach out directly.
            </p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link to="/blog" style={{
                padding: '10px 22px', background: 'var(--cyan)', color: '#080b10',
                borderRadius: 10, fontWeight: 600, fontSize: 14, display: 'inline-flex', alignItems: 'center', gap: 6,
              }}>Read the Blog <ArrowRight size={14} /></Link>
              <a href="mailto:parmeshwarpanwar777@gmail.com" style={{
                padding: '10px 22px', background: 'transparent', color: 'var(--text)',
                border: '1px solid var(--border)', borderRadius: 10, fontWeight: 500, fontSize: 14,
              }}>Get In Touch</a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
